import {
  getConfirmingRequestById,
  getConfirmingRequests,
} from 'lib/api/confirming';
import { ConfirmingRequestById } from 'lib/api/confirming/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import {
  hasAnyPermissions,
  showAlert,
  validatePermissions,
} from 'lib/functions/utils';
import { ConfirmingRequest } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import ExistingOperation from './components/ExistingOperation';
import Simulator from './components/Simulator';

type ConfirmingRequestFilter = {
  label: string;
  value: string;
};

const ConfirmingRequests = (props: { businessIdentifier?: string }) => {
  // Dispatch, user and loaders
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [miniLoading, setMiniLoading] = useState(false);

  // Confriming requests
  const [confirmingRequests, setConfirmingRequests] = useState<
    ConfirmingRequest[]
  >([]);
  const PAGE = 1;
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: PAGE,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  // New operation
  const [openNewOperation, setOpenNewOperation] = useState(false);

  // View details
  const [openRequest, setOpenRequest] = useState<ConfirmingRequestById | null>(
    null,
  );
  const [openExistingOperation, setOpenExistingOperation] = useState(false);

  // Edit operation
  const [editingRequest, setEditingRequest] =
    useState<ConfirmingRequestById | null>(null);
  const [openEditOperation, setOpenEditOperation] = useState(false);

  // Filters
  const [businessName, setBusinessName] = useState('');
  const [searchClient, setSearchClient] = useState(
    props.businessIdentifier ?? '',
  );
  const [searchRequestId, setSearchRequestId] = useState('');
  const [searchStatuses, setSearchStatuses] = useState<
    ConfirmingRequestFilter[]
  >([
    { label: 'En revisión', value: 'IN_REVIEW' },
    { label: 'Aceptada', value: 'ACCEPTED' },
    { label: 'En giro', value: 'IN_TRANSFER' },
  ]);
  const [selectedSortBy, setSelectedSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const filters: Filter[] = [
    {
      type: 'text',
      label: 'Rut deudor',
      value: searchClient,
      setter: setSearchClient,
    },
    {
      type: 'autocomplete',
      label: 'Nombre deudor',
      key: 'businessName',
      value: businessName,
      setter: setBusinessName,
    },
    {
      type: 'multiple',
      label: 'Estado',
      value: searchStatuses,
      setter: setSearchStatuses,
      options: [
        { label: 'En revisión', value: 'IN_REVIEW' },
        { label: 'Aceptada', value: 'ACCEPTED' },
        { label: 'Rechazada', value: 'REJECTED' },
        { label: 'En giro', value: 'IN_TRANSFER' },
        { label: 'Activa', value: 'ACTIVE' },
        { label: 'Completada', value: 'COMPLETED' },
      ],
    },
    {
      type: 'text',
      label: 'ID de solicitud',
      value: searchRequestId,
      setter: setSearchRequestId,
    },
  ];

  const handleNewRequest = () => {
    setOpenNewOperation(true);
  };

  const handleDetails = async (id: number) => {
    setMiniLoading(true);
    try {
      const confirmingRequest = await getConfirmingRequestById(dispatch, id);
      if (confirmingRequest) {
        setOpenRequest(confirmingRequest);
        setOpenExistingOperation(true);
        return setMiniLoading(false);
      }
    } catch (error) {
      setMiniLoading(false);
      return showAlert(dispatch, 'error', 'Ha ocurrido un error');
    }
  };

  const refreshOpenRequest = async (id: number) => {
    const updated = await getConfirmingRequestById(dispatch, id);
    if (updated) setOpenRequest(updated);
  };

  const handleEdit = async (id: number) => {
    setMiniLoading(true);
    try {
      const confirmingRequest = await getConfirmingRequestById(dispatch, id);
      if (confirmingRequest) {
        setEditingRequest(confirmingRequest);
        setOpenEditOperation(true);
        return setMiniLoading(false);
      }
    } catch (error) {
      setMiniLoading(false);
      return showAlert(dispatch, 'error', 'Ha ocurrido un error');
    }
  };

  const generateQuery = (page: number, pageSize: number): string => {
    let query = '';
    if (page) query += `page=${page}&`;
    if (pageSize) query += `pageSize=${pageSize}&`;
    if (searchClient) query += `stakeholderIdentifier=${searchClient}&`;
    if (searchRequestId) query += `id=${searchRequestId}&`;
    if (businessName) query += `stakeholderName=${businessName}&`;
    if (searchStatuses.length > 0) {
      let stringForQuery = '';
      for (const status of searchStatuses) {
        stringForQuery += `${status.value},`;
      }
      stringForQuery = stringForQuery.slice(0, -1);
      query += `statuses=${stringForQuery}&`;
    }
    if (selectedSortBy) query += `sortBy=${selectedSortBy}&`;
    if (sortOrder) query += `sortOrder=${sortOrder}&`;
    return query;
  };

  const getConfirmingRequestsFromServer = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    const query = generateQuery(page, pageSize);
    const response = await getConfirmingRequests(dispatch, query);
    setConfirmingRequests(response.data);
    setPagination(response.pagination);
    return response;
  };

  const triggerSearch = async () => {
    setLoading(true);
    await getConfirmingRequestsFromServer({
      page: 1,
      pageSize: PAGE_SIZE,
    });
    setLoading(false);
  };

  const setUp = async (id?: number) => {
    setLoading(true);
    try {
      await getConfirmingRequestsFromServer({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
    } catch (error) {
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    }

    setLoading(false);
    if (id) {
      await handleDetails(id);
    }
  };

  useEffect(() => {
    void setUp();
  }, []);

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'secondary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={triggerSearch}
          selectedSortBy={selectedSortBy}
          setSelectedSortBy={setSelectedSortBy}
          sortOptions={[
            { value: 'createdAt', label: 'Creado en' },
            { value: 'amount', label: 'Monto' },
            { value: 'id', label: 'ID' },
          ]}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          dispatch={dispatch}
        />
      ),
    },
    {
      action: handleNewRequest,
      name: 'Crear',
      color: 'primary',
      disabled: !validatePermissions(user, 'financing_confirming_create'),
    },
  ];

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        title={'Acá podrás ver todas tus solicitudes de confirming.'}
        variant={'body'}
        buttons={buttonsData}
      />
      {miniLoading && <CenteredCircularProgress type="layout" />}
      <TableWithPagination
        dataHeaders={[
          { key: 'id', label: 'ID' },
          { key: 'exception', label: 'Exc', type: 'boolean-exception-null' },
          { key: 'stakeholderName', label: 'Deudor' },
          { key: 'amount', label: 'Monto', type: 'money' },
          { key: 'income', label: 'Ingreso', type: 'good-money' },
          {
            key: 'minutesSinceUpdate',
            label: 'Estancado',
            type: 'formatMinutes',
          },
          {
            key: 'operationDate',
            label: 'Fecha de operación',
            type: 'date',
          },
          { key: 'createdAt', label: 'Creado en', type: 'date-large' },
          { key: 'status', label: 'Estado', type: 'status-chip' },
          ...(validatePermissions(user, 'businesses_read_all')
            ? [{ key: 'ownerName', label: 'Ejecutivo' }]
            : []),
          ...(hasAnyPermissions(user, [
            'businesses_read_all',
            'assign_confirming_risk_analyst',
            'be_confirming_risk_analyst',
          ])
            ? [{ key: 'riskAnalystName', label: 'Analista de riesgo' }]
            : []),
          ...(hasAnyPermissions(user, [
            'businesses_read_all',
            'assign_confirming_operations_analyst',
            'be_confirming_operations_analyst',
          ])
            ? [
                {
                  key: 'operationsAnalystName',
                  label: 'Analista de operaciones',
                },
              ]
            : []),
        ]}
        data={confirmingRequests}
        setData={setConfirmingRequests}
        searchData={getConfirmingRequestsFromServer}
        pagination={pagination}
        setPagination={setPagination}
        showActions
        rowsPerPageDefault={PAGE_SIZE}
        exportToFile="xlsx"
        lastElement
        finalSumRow
        cantSelectRow
        handleClickRow={handleDetails}
      />
      {openNewOperation && (
        <Simulator
          open={openNewOperation}
          setOpen={(open: boolean) => {
            setOpenNewOperation(open);
            if (!open) {
              setEditingRequest(null);
            }
          }}
          parentSetUp={() => void setUp()}
          editingRequest={null}
        />
      )}
      {openExistingOperation && (
        <ExistingOperation
          open={openExistingOperation}
          setOpen={setOpenExistingOperation}
          selectedRequest={openRequest}
          handleEdit={handleEdit}
          parentSetUp={setUp}
          refreshOpenRequest={refreshOpenRequest}
        />
      )}
      {openEditOperation && (
        <Simulator
          open={openEditOperation}
          setOpen={(open: boolean) => {
            setOpenEditOperation(open);
            if (!open) {
              setEditingRequest(null);
            }
          }}
          parentSetUp={() => void setUp()}
          editingRequest={editingRequest}
        />
      )}
    </div>
  );
};

export default ConfirmingRequests;
